import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { Home, LayoutGrid, Plus, DollarSign, Menu, X, User, BarChart3, MessageSquare, Wallet } from "lucide-react";

interface NavigationItemProps {
  href: string;
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

const NavigationItem: React.FC<NavigationItemProps> = ({ href, icon, label, isActive, onClick }) => {
  return (
    <a
      href={href}
      onClick={e => {
        e.preventDefault();
        onClick?.();
      }}
      className={cn(
        "flex flex-col items-center gap-1",
        "transition-colors duration-200",
        isActive ? "text-accent" : "text-gray-400 hover:text-accent"
      )}
    >
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </a>
  );
};

export const MobileNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40">
        {/* Glassmorphism background */}
        <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-lg border-t border-gray-800" />

        {/* Navigation content */}
        <div className="relative px-6 py-2">
          <div className="flex items-center justify-between">
            <NavigationItem
              href="/dashboard"
              icon={<Home size={20} />}
              label={t("navigation.home")}
              isActive={location.pathname === "/dashboard"}
              onClick={() => handleNavigation("/dashboard")}
            />

            <NavigationItem
              href="/budget"
              icon={<DollarSign size={20} />}
              label={t("navigation.budget")}
              isActive={location.pathname === "/budget"}
              onClick={() => handleNavigation("/budget")}
            />

            {/* Center Add Button */}
            <div className="relative -top-6">
              <button
                onClick={() => handleNavigation("/expenses/new")}
                className="h-14 w-14 rounded-full bg-accent-soft hover:bg-accent 
                         text-white shadow-lg flex items-center justify-center
                         transition-transform hover:scale-105 active:scale-95"
                aria-label="Add new expense"
              >
                <Plus size={24} />
              </button>
            </div>

            <NavigationItem
              href="/movements"
              icon={<LayoutGrid size={20} />}
              label={t("navigation.movements")}
              isActive={location.pathname === "/movements"}
              onClick={() => handleNavigation("/movements")}
            />

            <button
              onClick={() => setIsMenuOpen(true)}
              className="flex flex-col items-center gap-1 text-gray-400 hover:text-accent"
            >
              <Menu size={20} />
              <span className="text-xs font-medium">Menú</span>
            </button>
          </div>
        </div>
      </div>

      {/* Slide-over Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/40 transition-opacity" onClick={() => setIsMenuOpen(false)} />

          {/* Slide-over panel */}
          <div className="fixed right-0 top-0 h-full w-72 bg-gray-900 transform transition-transform">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-end p-4">
                <button onClick={() => setIsMenuOpen(false)} className="p-2 text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              {/* Menu Items */}
              <div className="flex-1 px-4 pb-6">
                <div className="space-y-6">
                  {/* Overview Section */}
                  <div>
                    <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Overview</h3>
                    <div className="space-y-1">
                      <a
                        href="/analysis"
                        onClick={e => {
                          e.preventDefault();
                          handleNavigation("/analysis");
                        }}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-300 
                                 hover:text-white hover:bg-gray-800/50 rounded-lg"
                      >
                        <BarChart3 size={20} />
                        <span>{t("navigation.analysis")}</span>
                      </a>
                      <a
                        href="/chat"
                        onClick={e => {
                          e.preventDefault();
                          handleNavigation("/chat");
                        }}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-300 
                                 hover:text-white hover:bg-gray-800/50 rounded-lg"
                      >
                        <MessageSquare size={20} />
                        <span>{t("navigation.chat")}</span>
                      </a>
                      <a
                        href="/wallet"
                        onClick={e => {
                          e.preventDefault();
                          handleNavigation("/wallet");
                        }}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-300 
                                 hover:text-white hover:bg-gray-800/50 rounded-lg"
                      >
                        <Wallet size={20} />
                        <span>{t("navigation.wallet")}</span>
                      </a>
                    </div>
                  </div>

                  {/* Profile Section */}
                  <div>
                    <h3 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Profile</h3>
                    <div className="space-y-1">
                      <a
                        href="/profile"
                        onClick={e => {
                          e.preventDefault();
                          handleNavigation("/profile");
                        }}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-300 
                                 hover:text-white hover:bg-gray-800/50 rounded-lg"
                      >
                        <User size={20} />
                        <span>{t("navigation.profile")}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
